import React from 'react';
import CollapsiblePanel, { type CollapsiblePanelProps } from '@/components/common/CollapsiblePanel';
import { cx } from '@/ui/utils/cx';
import { typography } from '@/ui/theme/tokens';

export type AppSectionProps = {
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  badge?: React.ReactNode;
  className?: string;
  bodyClassName?: string;
  children: React.ReactNode;
};

/** Non-collapsible section panel */
export function AppSection({
  title,
  description,
  actions,
  badge,
  className,
  bodyClassName,
  children,
}: AppSectionProps) {
  return (
    <section className={cx('app-section', className)}>
      <header className="app-section__header">
        <div className="app-section__titles">
          <h2 className={typography.sectionTitle}>{title}</h2>
          {description ? (
            <p className={typography.sectionDescription}>{description}</p>
          ) : null}
        </div>
        {badge != null && badge !== false ? (
          <span className="collapsible-panel__badge">{badge}</span>
        ) : null}
        {actions ? <div className="app-section__actions">{actions}</div> : null}
      </header>
      <div className={cx('app-section__body', bodyClassName)}>{children}</div>
    </section>
  );
}

export type AppAccordionProps = CollapsiblePanelProps & {
  /** When false, renders AppSection instead of collapsible panel */
  collapsible?: boolean;
  description?: string;
};

/** Collapsible section — wraps shared CollapsiblePanel with design-system defaults */
export default function AppAccordion({
  collapsible = true,
  description,
  subtitle,
  className,
  ...rest
}: AppAccordionProps) {
  if (!collapsible) {
    return (
      <AppSection
        title={rest.title}
        description={description ?? subtitle}
        actions={rest.actions}
        badge={rest.badge}
        className={className}
      >
        {rest.children}
      </AppSection>
    );
  }

  return (
    <CollapsiblePanel
      {...rest}
      unmountWhenCollapsed={rest.unmountWhenCollapsed ?? true}
      subtitle={subtitle ?? description}
      className={cx('app-accordion', className)}
    />
  );
}
